import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  createTheme,
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider as JssThemeProvider } from 'react-jss';
import { DarkTheme, LightTheme } from '../../theme';
import {
  AppThemeContext,
  ColorMode,
  getColorMode,
  setColorMode as persistColorMode,
} from './helpers';

interface Props {
  children: ReactNode;
}

const getColorModeDefault = (prefersDarkMode: boolean): ColorMode => {
  const storedDefault = getColorMode();
  return storedDefault || (prefersDarkMode ? 'dark' : 'light');
};

export const AppThemeProvider: FC<Props> = ({ children }) => {
  // @todo load default from browser storage, then from prefersDarkMode
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [colorMode, setColorMode] = useState<ColorMode>(getColorModeDefault(prefersDarkMode));

  const handleSetColorMode = useCallback(
    (mode: ColorMode) => {
      setColorMode(mode);
      persistColorMode(mode);
      document.documentElement.setAttribute('data-theme', mode);
    },
    [setColorMode]
  );

  const activeTheme = useMemo(() => {
    return createTheme(colorMode === 'light' ? LightTheme : DarkTheme);
  }, [colorMode]);

  const isDarkMode = colorMode === 'dark';

  return (
    <AppThemeContext.Provider value={{ colorMode, isDarkMode, setColorMode: handleSetColorMode }}>
      <MuiThemeProvider theme={activeTheme}>
        <CssBaseline />
        <JssThemeProvider theme={activeTheme}>{children}</JssThemeProvider>
      </MuiThemeProvider>
    </AppThemeContext.Provider>
  );
};
